// src/components/pipelines/overlays/PropertiesPanel.jsx - Floating properties panel for selected nodes
import React, { useState } from 'react';
import { Button } from '../../ui/button.tsx';
import { Input } from '../../ui/input.tsx';
import { Label } from '../../ui/label.tsx';
import { Badge } from '../../ui/badge.tsx';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select.tsx';
import { Textarea } from '../../ui/textarea.tsx';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '../../ui/collapsible.tsx';
import { X, Plus, Trash2, ExternalLink, ChevronDown, ChevronRight, Settings } from 'lucide-react';

/**
 * PropertiesPanel - Floating panel for editing selected node properties
 *
 * Props:
 * - selectedNode: currently selected node object
 * - onClose: callback when panel is closed
 * - onUpdateNode: callback when node properties are updated
 * - onEditKanban: callback when "Edit Full Board" is clicked for kanban
 */
const PropertiesPanel = ({
  selectedNode = null,
  onClose,
  onUpdateNode,
  onEditKanban
}) => {
  const [editedData, setEditedData] = useState(selectedNode?.data || {});

  // Update local state when selected node changes
  React.useEffect(() => {
    setEditedData(selectedNode?.data || {});
  }, [selectedNode]);

  if (!selectedNode) {
    return null;
  }

  const handleUpdate = () => {
    onUpdateNode?.(selectedNode.id, editedData);
  };

  const updateField = (field, value) => {
    setEditedData(prev => ({ ...prev, [field]: value }));
  };

  const updateConfig = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      config: { ...(prev.config || {}), [field]: value }
    }));
  };

  const addCondition = () => {
    setEditedData(prev => ({
      ...prev,
      conditions: [
        ...(prev.conditions || []),
        { field: '', operator: 'equals', value: '' }
      ]
    }));
  };

  const removeCondition = (index) => {
    setEditedData(prev => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== index)
    }));
  };

  const updateCondition = (index, field, value) => {
    setEditedData(prev => ({
      ...prev,
      conditions: prev.conditions.map((cond, i) =>
        i === index ? { ...cond, [field]: value } : cond
      )
    }));
  };

  // Render different content based on node type
  const renderContent = () => {
    switch (selectedNode.type) {
      case 'kanban':
        return <KanbanProperties data={editedData} updateField={updateField} onEditKanban={onEditKanban} />;
      case 'logicGate':
        return <LogicGateProperties data={editedData} updateField={updateField} addCondition={addCondition} removeCondition={removeCondition} updateCondition={updateCondition} />;
      case 'wait':
        return <WaitProperties data={editedData} updateField={updateField} />;
      case 'entry':
        return <EntryProperties data={editedData} updateField={updateField} addCondition={addCondition} removeCondition={removeCondition} updateCondition={updateCondition} />;
      case 'exit':
        return <ExitProperties data={editedData} updateField={updateField} />;
      default:
        return <div className="text-sm text-slate-400">Unknown node type</div>;
    }
  };

  return (
    <div className="absolute top-4 right-4 bottom-4 z-50 w-96">
      <Card className="h-full flex flex-col bg-slate-900/95 backdrop-blur-md border-slate-700 shadow-2xl">
        <CardHeader className="pb-3 border-b border-slate-700/50">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base text-slate-100">Properties</CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-6 w-6 p-0 text-slate-400 hover:text-slate-100"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          <Badge variant="secondary" className="text-xs w-fit mt-2">
            {selectedNode.type === 'kanban' && '📋 Kanban Board'}
            {selectedNode.type === 'logicGate' && (editedData.gateType === 'AND' ? '◆ AND Gate' : '◇ OR Gate')}
            {selectedNode.type === 'wait' && '⏱️ Wait Node'}
            {selectedNode.type === 'entry' && '🎯 Entry Point'}
            {selectedNode.type === 'exit' && '🏁 Exit Point'}
          </Badge>
        </CardHeader>

        <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
          {renderContent()}
        </CardContent>

        <div className="p-4 border-t border-slate-700/50">
          <Button
            onClick={handleUpdate}
            className="w-full bg-purple-600 hover:bg-purple-700"
          >
            Update Node
          </Button>
        </div>
      </Card>
    </div>
  );
};

// Kanban Properties Component
const KanbanProperties = ({ data, updateField, onEditKanban }) => {
  const [expandedStage, setExpandedStage] = React.useState(null);

  const updateStageConfig = (stageId, field, value) => {
    const stages = data.stages || [];
    const updatedStages = stages.map(stage => {
      if (stage.id === stageId) {
        return {
          ...stage,
          config: {
            ...(stage.config || {}),
            [field]: value
          }
        };
      }
      return stage;
    });
    updateField('stages', updatedStages);
  };

  const addAdvanceRule = (stageId) => {
    const stages = data.stages || [];
    const updatedStages = stages.map(stage => {
      if (stage.id === stageId) {
        const advanceRules = stage.config?.advanceRules || [];
        return {
          ...stage,
          config: {
            ...(stage.config || {}),
            advanceRules: [...advanceRules, { field: '', operator: '==', value: '', delay: 0, delayUnit: 'days' }]
          }
        };
      }
      return stage;
    });
    updateField('stages', updatedStages);
  };

  const removeAdvanceRule = (stageId, ruleIndex) => {
    const stages = data.stages || [];
    const updatedStages = stages.map(stage => {
      if (stage.id === stageId) {
        const advanceRules = (stage.config?.advanceRules || []).filter((_, i) => i !== ruleIndex);
        return {
          ...stage,
          config: {
            ...(stage.config || {}),
            advanceRules
          }
        };
      }
      return stage;
    });
    updateField('stages', updatedStages);
  };

  const updateAdvanceRule = (stageId, ruleIndex, field, value) => {
    const stages = data.stages || [];
    const updatedStages = stages.map(stage => {
      if (stage.id === stageId) {
        const advanceRules = (stage.config?.advanceRules || []).map((rule, i) =>
          i === ruleIndex ? { ...rule, [field]: value } : rule
        );
        return {
          ...stage,
          config: {
            ...(stage.config || {}),
            advanceRules
          }
        };
      }
      return stage;
    });
    updateField('stages', updatedStages);
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-sm font-medium text-slate-300">Board Name</Label>
        <Input
          value={data.name || ''}
          onChange={(e) => updateField('name', e.target.value)}
          className="bg-slate-800 border-slate-700 text-slate-100"
          placeholder="e.g., Onboarding"
        />
      </div>

      <div className="space-y-2">
        <Label className="text-sm font-medium text-slate-300">Status</Label>
        <Select
          value={data.isActive ? 'active' : 'inactive'}
          onValueChange={(value) => updateField('isActive', value === 'active')}
        >
          <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-100">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700">
            <SelectItem value="active" className="text-slate-100 focus:bg-slate-700">
              Active
            </SelectItem>
            <SelectItem value="inactive" className="text-slate-100 focus:bg-slate-700">
              Inactive
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="pt-4 border-t border-slate-700/50 space-y-2">
        <div className="text-xs font-medium text-slate-400 uppercase tracking-wide">
          Board Details
        </div>
        <div className="space-y-1 text-sm">
          <div className="flex justify-between text-slate-300">
            <span>Stages:</span>
            <span className="font-medium">{data.stages?.length || 0}</span>
          </div>
          <div className="flex justify-between text-slate-300">
            <span>Total Customers:</span>
            <span className="font-medium text-purple-300">{data.totalCustomers || 0}</span>
          </div>
        </div>
      </div>

      {/* Stage Configuration Section */}
      {data.stages && data.stages.length > 0 && (
        <div className="pt-4 border-t border-slate-700/50 space-y-3">
          <div className="flex items-center gap-2">
            <Settings className="w-3 h-3 text-slate-400" />
            <Label className="text-sm font-medium text-slate-300">Stage Configuration</Label>
          </div>
          <div className="space-y-2">
            {data.stages.map((stage, index) => (
              <Collapsible
                key={stage.id}
                open={expandedStage === stage.id}
                onOpenChange={(open) => setExpandedStage(open ? stage.id : null)}
              >
                <CollapsibleTrigger asChild>
                  <button className="w-full flex items-center justify-between p-2 bg-slate-800/50 hover:bg-slate-800 border border-slate-700/50 rounded-md text-left transition-colors">
                    <div className="flex items-center gap-2">
                      {expandedStage === stage.id ? (
                        <ChevronDown className="w-3 h-3 text-slate-400" />
                      ) : (
                        <ChevronRight className="w-3 h-3 text-slate-400" />
                      )}
                      <span className="text-sm font-medium text-slate-300">{stage.name}</span>
                      <Badge variant="outline" className="text-xs">
                        Stage {index + 1}
                      </Badge>
                    </div>
                    {stage.config?.advanceRules?.length > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        {stage.config.advanceRules.length} rule{stage.config.advanceRules.length !== 1 ? 's' : ''}
                      </Badge>
                    )}
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-2 space-y-3">
                  <div className="pl-5 pr-2 pb-2">
                    {/* Auto-Advance Rules */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs font-medium text-slate-400 uppercase">
                          Auto-Advance Rules
                        </Label>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => addAdvanceRule(stage.id)}
                          className="h-6 text-xs text-purple-300 hover:text-purple-100"
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Add Rule
                        </Button>
                      </div>

                      {(stage.config?.advanceRules || []).map((rule, ruleIndex) => (
                        <div key={ruleIndex} className="bg-slate-900/50 border border-slate-700/30 rounded-md p-2 space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-slate-500">Rule {ruleIndex + 1}</span>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => removeAdvanceRule(stage.id, ruleIndex)}
                              className="h-4 w-4 p-0 text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>

                          <Input
                            placeholder="Field (e.g., days_in_stage)"
                            value={rule.field || ''}
                            onChange={(e) => updateAdvanceRule(stage.id, ruleIndex, 'field', e.target.value)}
                            className="bg-slate-800 border-slate-700 text-slate-100 text-xs h-7"
                          />

                          <div className="grid grid-cols-2 gap-2">
                            <Select
                              value={rule.operator || '=='}
                              onValueChange={(value) => updateAdvanceRule(stage.id, ruleIndex, 'operator', value)}
                            >
                              <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-100 text-xs h-7">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-slate-800 border-slate-700">
                                <SelectItem value="==" className="text-slate-100 focus:bg-slate-700">=</SelectItem>
                                <SelectItem value=">" className="text-slate-100 focus:bg-slate-700">&gt;</SelectItem>
                                <SelectItem value="<" className="text-slate-100 focus:bg-slate-700">&lt;</SelectItem>
                                <SelectItem value=">=" className="text-slate-100 focus:bg-slate-700">≥</SelectItem>
                                <SelectItem value="<=" className="text-slate-100 focus:bg-slate-700">≤</SelectItem>
                              </SelectContent>
                            </Select>

                            <Input
                              placeholder="Value"
                              value={rule.value || ''}
                              onChange={(e) => updateAdvanceRule(stage.id, ruleIndex, 'value', e.target.value)}
                              className="bg-slate-800 border-slate-700 text-slate-100 text-xs h-7"
                            />
                          </div>

                          <div className="flex gap-2">
                            <Input
                              type="number"
                              min="0"
                              placeholder="Delay"
                              value={rule.delay || 0}
                              onChange={(e) => updateAdvanceRule(stage.id, ruleIndex, 'delay', parseInt(e.target.value) || 0)}
                              className="bg-slate-800 border-slate-700 text-slate-100 text-xs h-7 flex-1"
                            />
                            <Select
                              value={rule.delayUnit || 'days'}
                              onValueChange={(value) => updateAdvanceRule(stage.id, ruleIndex, 'delayUnit', value)}
                            >
                              <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-100 text-xs h-7 w-24">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-slate-800 border-slate-700">
                                <SelectItem value="hours" className="text-slate-100 focus:bg-slate-700">hours</SelectItem>
                                <SelectItem value="days" className="text-slate-100 focus:bg-slate-700">days</SelectItem>
                                <SelectItem value="weeks" className="text-slate-100 focus:bg-slate-700">weeks</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      ))}

                      {(!stage.config?.advanceRules || stage.config.advanceRules.length === 0) && (
                        <div className="text-xs text-slate-500 italic text-center py-3 bg-slate-900/30 rounded border border-dashed border-slate-700/30">
                          No auto-advance rules - manual only
                        </div>
                      )}
                    </div>

                    {/* Actions - Coming Soon */}
                    <div className="pt-3 mt-3 border-t border-slate-700/30">
                      <Label className="text-xs font-medium text-slate-400 uppercase mb-2 block">
                        Stage Actions (Coming Soon)
                      </Label>
                      <div className="text-xs text-slate-500 italic text-center py-2 bg-slate-900/30 rounded border border-dashed border-slate-700/30">
                        Configure actions for stage entry/exit
                      </div>
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            ))}
          </div>
        </div>
      )}

      {data.pipelineId && (
        <Button
          variant="outline"
          className="w-full border-slate-700 text-slate-300 hover:bg-slate-800"
          onClick={() => onEditKanban?.(data.pipelineId)}
        >
          <ExternalLink className="w-4 h-4 mr-2" />
          Edit Full Board
        </Button>
      )}
    </div>
  );
};

// Logic Gate Properties Component
const LogicGateProperties = ({ data, updateField, addCondition, removeCondition, updateCondition }) => {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-sm font-medium text-slate-300">Gate Name</Label>
        <Input
          value={data.name || ''}
          onChange={(e) => updateField('name', e.target.value)}
          className="bg-slate-800 border-slate-700 text-slate-100"
          placeholder="e.g., Check VIP Status"
        />
      </div>

      <div className="space-y-2">
        <Label className="text-sm font-medium text-slate-300">Gate Type</Label>
        <Select
          value={data.gateType || 'AND'}
          onValueChange={(value) => updateField('gateType', value)}
        >
          <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-100">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700">
            <SelectItem value="AND" className="text-slate-100 focus:bg-slate-700">
              ◆ AND - All conditions must be true
            </SelectItem>
            <SelectItem value="OR" className="text-slate-100 focus:bg-slate-700">
              ◇ OR - Any condition can be true
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="pt-4 border-t border-slate-700/50 space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium text-slate-300">Conditions</Label>
          <Button
            size="sm"
            variant="ghost"
            onClick={addCondition}
            className="h-7 text-xs text-purple-300 hover:text-purple-100"
          >
            <Plus className="w-3 h-3 mr-1" />
            Add
          </Button>
        </div>

        <div className="space-y-2">
          {(data.conditions || []).map((condition, index) => (
            <div key={index} className="bg-slate-800/50 border border-slate-700/50 rounded-md p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-slate-400">Condition {index + 1}</span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => removeCondition(index)}
                  className="h-5 w-5 p-0 text-red-400 hover:text-red-300"
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>

              <Input
                placeholder="Field (e.g., is_vip)"
                value={condition.field || ''}
                onChange={(e) => updateCondition(index, 'field', e.target.value)}
                className="bg-slate-800 border-slate-700 text-slate-100 text-xs h-8"
              />

              <Select
                value={condition.operator || 'equals'}
                onValueChange={(value) => updateCondition(index, 'operator', value)}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-100 text-xs h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="equals" className="text-slate-100 focus:bg-slate-700">equals</SelectItem>
                  <SelectItem value="not_equals" className="text-slate-100 focus:bg-slate-700">not equals</SelectItem>
                  <SelectItem value="greater_than" className="text-slate-100 focus:bg-slate-700">greater than</SelectItem>
                  <SelectItem value="less_than" className="text-slate-100 focus:bg-slate-700">less than</SelectItem>
                  <SelectItem value="contains" className="text-slate-100 focus:bg-slate-700">contains</SelectItem>
                </SelectContent>
              </Select>

              <Input
                placeholder="Value"
                value={condition.value || ''}
                onChange={(e) => updateCondition(index, 'value', e.target.value)}
                className="bg-slate-800 border-slate-700 text-slate-100 text-xs h-8"
              />
            </div>
          ))}

          {(!data.conditions || data.conditions.length === 0) && (
            <div className="text-xs text-slate-500 italic text-center py-4">
              No conditions added yet
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Action Properties Component
const ActionProperties = ({ data, updateField, updateConfig }) => {
  const { actionType = 'send_email', config = {} } = data;

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-sm font-medium text-slate-300">Action Name</Label>
        <Input
          value={data.name || ''}
          onChange={(e) => updateField('name', e.target.value)}
          className="bg-slate-800 border-slate-700 text-slate-100"
          placeholder="e.g., Send Welcome Email"
        />
      </div>

      <div className="space-y-2">
        <Label className="text-sm font-medium text-slate-300">Action Type</Label>
        <Select
          value={actionType}
          onValueChange={(value) => updateField('actionType', value)}
        >
          <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-100">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700">
            <SelectItem value="send_email" className="text-slate-100 focus:bg-slate-700">
              📧 Send Email
            </SelectItem>
            <SelectItem value="send_sms" className="text-slate-100 focus:bg-slate-700">
              📱 Send SMS
            </SelectItem>
            <SelectItem value="wait" className="text-slate-100 focus:bg-slate-700">
              ⏱️ Wait
            </SelectItem>
            <SelectItem value="webhook" className="text-slate-100 focus:bg-slate-700">
              🔔 Webhook
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="pt-4 border-t border-slate-700/50 space-y-3">
        <Label className="text-sm font-medium text-slate-300">Configuration</Label>

        {actionType === 'send_email' && (
          <>
            <div className="space-y-2">
              <Label className="text-xs text-slate-400">Email Template</Label>
              <Input
                placeholder="Template name or ID"
                value={config.template || ''}
                onChange={(e) => updateConfig('template', e.target.value)}
                className="bg-slate-800 border-slate-700 text-slate-100 text-sm"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-slate-400">Subject</Label>
              <Input
                placeholder="Email subject"
                value={config.subject || ''}
                onChange={(e) => updateConfig('subject', e.target.value)}
                className="bg-slate-800 border-slate-700 text-slate-100 text-sm"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-slate-400">To (optional)</Label>
              <Input
                placeholder="recipient@example.com"
                value={config.to || ''}
                onChange={(e) => updateConfig('to', e.target.value)}
                className="bg-slate-800 border-slate-700 text-slate-100 text-sm"
              />
            </div>
          </>
        )}

        {actionType === 'send_sms' && (
          <div className="space-y-2">
            <Label className="text-xs text-slate-400">Message</Label>
            <Textarea
              placeholder="SMS message content"
              value={config.message || ''}
              onChange={(e) => updateConfig('message', e.target.value)}
              className="bg-slate-800 border-slate-700 text-slate-100 text-sm resize-none"
              rows={4}
            />
          </div>
        )}

        {actionType === 'wait' && (
          <div className="space-y-2">
            <Label className="text-xs text-slate-400">Wait Duration (days)</Label>
            <Input
              type="number"
              min="1"
              placeholder="1"
              value={config.days || ''}
              onChange={(e) => updateConfig('days', parseInt(e.target.value) || 1)}
              className="bg-slate-800 border-slate-700 text-slate-100 text-sm"
            />
          </div>
        )}

        {actionType === 'webhook' && (
          <>
            <div className="space-y-2">
              <Label className="text-xs text-slate-400">Webhook URL</Label>
              <Input
                placeholder="https://example.com/webhook"
                value={config.url || ''}
                onChange={(e) => updateConfig('url', e.target.value)}
                className="bg-slate-800 border-slate-700 text-slate-100 text-sm"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-slate-400">Method</Label>
              <Select
                value={config.method || 'POST'}
                onValueChange={(value) => updateConfig('method', value)}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-100 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="POST" className="text-slate-100 focus:bg-slate-700">POST</SelectItem>
                  <SelectItem value="GET" className="text-slate-100 focus:bg-slate-700">GET</SelectItem>
                  <SelectItem value="PUT" className="text-slate-100 focus:bg-slate-700">PUT</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Wait Properties Component
const WaitProperties = ({ data, updateField }) => {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-sm font-medium text-slate-300">Duration</Label>
        <div className="flex gap-2">
          <Input
            type="number"
            min="1"
            value={data.duration || 1}
            onChange={(e) => updateField('duration', parseInt(e.target.value) || 1)}
            className="bg-slate-800 border-slate-700 text-slate-100 flex-1"
            placeholder="1"
          />
          <Select
            value={data.unit || 'days'}
            onValueChange={(value) => updateField('unit', value)}
          >
            <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-100 w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="minutes" className="text-slate-100 focus:bg-slate-700">
                Minutes
              </SelectItem>
              <SelectItem value="hours" className="text-slate-100 focus:bg-slate-700">
                Hours
              </SelectItem>
              <SelectItem value="days" className="text-slate-100 focus:bg-slate-700">
                Days
              </SelectItem>
              <SelectItem value="weeks" className="text-slate-100 focus:bg-slate-700">
                Weeks
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-sm font-medium text-slate-300">Description</Label>
        <Textarea
          value={data.description || ''}
          onChange={(e) => updateField('description', e.target.value)}
          className="bg-slate-800 border-slate-700 text-slate-100 resize-none"
          placeholder="Optional: Describe what this wait is for..."
          rows={3}
        />
      </div>

      <div className="pt-4 border-t border-slate-700/50">
        <div className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">
          Preview
        </div>
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-md p-3 text-center">
          <div className="text-2xl font-bold text-yellow-200">
            {data.duration || 1}
          </div>
          <div className="text-sm text-yellow-400 uppercase tracking-wider">
            {data.unit || 'days'}
          </div>
          {data.description && (
            <div className="text-xs text-slate-400 italic mt-2">
              {data.description}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Entry Properties Component
const EntryProperties = ({ data, updateField, addCondition, removeCondition, updateCondition }) => {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-sm font-medium text-slate-300">Trigger Type</Label>
        <Select
          value={data.triggerType || 'system_event'}
          onValueChange={(value) => updateField('triggerType', value)}
        >
          <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-100">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700">
            <SelectItem value="system_event" className="text-slate-100 focus:bg-slate-700">
              System Event
            </SelectItem>
            <SelectItem value="time_based" className="text-slate-100 focus:bg-slate-700">
              Time-Based Schedule
            </SelectItem>
            <SelectItem value="manual" className="text-slate-100 focus:bg-slate-700">
              Manual Trigger
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {data.triggerType === 'system_event' && (
        <div className="space-y-2">
          <Label className="text-sm font-medium text-slate-300">Event Type</Label>
          <Select
            value={data.eventType || 'customer_created'}
            onValueChange={(value) => updateField('eventType', value)}
          >
            <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-100">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="customer_created" className="text-slate-100 focus:bg-slate-700">
                Customer Created
              </SelectItem>
              <SelectItem value="invoice_paid" className="text-slate-100 focus:bg-slate-700">
                Invoice Paid
              </SelectItem>
              <SelectItem value="invoice_overdue" className="text-slate-100 focus:bg-slate-700">
                Invoice Overdue
              </SelectItem>
              <SelectItem value="appointment_completed" className="text-slate-100 focus:bg-slate-700">
                Appointment Completed
              </SelectItem>
              <SelectItem value="stage_entered" className="text-slate-100 focus:bg-slate-700">
                Stage Entered
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="space-y-2">
        <Label className="text-sm font-medium text-slate-300">Description</Label>
        <Textarea
          value={data.description || ''}
          onChange={(e) => updateField('description', e.target.value)}
          className="bg-slate-800 border-slate-700 text-slate-100 resize-none"
          placeholder="Optional: Describe when this flow starts..."
          rows={2}
        />
      </div>

      <div className="pt-4 border-t border-slate-700/50 space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium text-slate-300">Entry Conditions</Label>
          <Button
            size="sm"
            variant="ghost"
            onClick={addCondition}
            className="h-7 text-xs text-green-300 hover:text-green-100"
          >
            <Plus className="w-3 h-3 mr-1" />
            Add
          </Button>
        </div>

        <div className="space-y-2">
          {(data.conditions || []).map((condition, index) => (
            <div key={index} className="bg-slate-800/50 border border-slate-700/50 rounded-md p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-slate-400">Condition {index + 1}</span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => removeCondition(index)}
                  className="h-5 w-5 p-0 text-red-400 hover:text-red-300"
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>

              <Input
                placeholder="Field (e.g., is_new_customer)"
                value={condition.field || ''}
                onChange={(e) => updateCondition(index, 'field', e.target.value)}
                className="bg-slate-800 border-slate-700 text-slate-100 text-xs h-8"
              />

              <Select
                value={condition.operator || '=='}
                onValueChange={(value) => updateCondition(index, 'operator', value)}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-100 text-xs h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="==" className="text-slate-100 focus:bg-slate-700">==</SelectItem>
                  <SelectItem value="!=" className="text-slate-100 focus:bg-slate-700">!=</SelectItem>
                  <SelectItem value=">" className="text-slate-100 focus:bg-slate-700">&gt;</SelectItem>
                  <SelectItem value="<" className="text-slate-100 focus:bg-slate-700">&lt;</SelectItem>
                  <SelectItem value="contains" className="text-slate-100 focus:bg-slate-700">contains</SelectItem>
                </SelectContent>
              </Select>

              <Input
                placeholder="Value"
                value={condition.value || ''}
                onChange={(e) => updateCondition(index, 'value', e.target.value)}
                className="bg-slate-800 border-slate-700 text-slate-100 text-xs h-8"
              />
            </div>
          ))}

          {(!data.conditions || data.conditions.length === 0) && (
            <div className="text-xs text-slate-500 italic text-center py-4">
              No conditions - all {data.eventType?.replace(/_/g, ' ') || 'events'} will trigger
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Exit Properties Component
const ExitProperties = ({ data, updateField }) => {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-sm font-medium text-slate-300">Exit Label</Label>
        <Input
          value={data.label || 'Complete'}
          onChange={(e) => updateField('label', e.target.value)}
          className="bg-slate-800 border-slate-700 text-slate-100"
          placeholder="e.g., Review Received"
        />
      </div>

      <div className="space-y-2">
        <Label className="text-sm font-medium text-slate-300">Exit Type</Label>
        <Select
          value={data.exitType || 'success'}
          onValueChange={(value) => updateField('exitType', value)}
        >
          <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-100">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700">
            <SelectItem value="success" className="text-slate-100 focus:bg-slate-700">
              ✓ Success
            </SelectItem>
            <SelectItem value="failure" className="text-slate-100 focus:bg-slate-700">
              ✗ Failure
            </SelectItem>
            <SelectItem value="neutral" className="text-slate-100 focus:bg-slate-700">
              • Neutral
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="text-sm font-medium text-slate-300">Description</Label>
        <Textarea
          value={data.description || ''}
          onChange={(e) => updateField('description', e.target.value)}
          className="bg-slate-800 border-slate-700 text-slate-100 resize-none"
          placeholder="Optional: Describe what this exit means..."
          rows={2}
        />
      </div>

      <div className="pt-4 border-t border-slate-700/50">
        <div className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">
          Exit Actions (Coming Soon)
        </div>
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-md p-3 text-xs text-slate-500 italic text-center">
          Configure actions to run when customers reach this exit
        </div>
      </div>

      <div className="pt-2">
        <div className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">
          Preview
        </div>
        <div className={`
          border rounded-md p-3
          ${data.exitType === 'success' ? 'bg-green-500/10 border-green-500/20' : ''}
          ${data.exitType === 'failure' ? 'bg-red-500/10 border-red-500/20' : ''}
          ${data.exitType === 'neutral' ? 'bg-slate-500/10 border-slate-500/20' : ''}
        `}>
          <div className={`
            text-sm font-medium text-center
            ${data.exitType === 'success' ? 'text-green-200' : ''}
            ${data.exitType === 'failure' ? 'text-red-200' : ''}
            ${data.exitType === 'neutral' ? 'text-slate-200' : ''}
          `}>
            {data.label || 'Complete'}
          </div>
          {data.description && (
            <div className="text-xs text-slate-400 italic text-center mt-1">
              {data.description}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PropertiesPanel;
